<!--
 * @Description: 用户信息下拉
 * @Author: ljrong
 * @Date: 2024-04-11 15:58:32
 * @LastEditors: Xiaochun liu
 * @LastEditTime: 2025-03-07 16:31:54
-->
<script lang="tsx" setup>
import { computed } from 'vue'
import UserDropEnterpriseList from './UserDropEnterpriseList.vue'
import defaultAvatar from '@/assets/default-avatar.png'
import { WorkspaceType, useAuthStore, useUserStore, useWorkspaceStore } from '@/store'
import coworkCodeImage from '@/assets/cowork-code.png'
import { openWindowWithWorkspace } from '@/utils/tool'
import { logout } from '@/api'

const emits = defineEmits(['Enter', 'Leave', 'inviteMember'])

const userStore = useUserStore()
const workspaceStore = useWorkspaceStore()
const authStore = useAuthStore()
const router = useRouter()
const route = useRoute()

const isPersonal = computed(() => workspaceStore.isPersonal)
// 工作空间列表 包含个人空间
const workspaceList = computed(() => workspaceStore.workspaceList)
const currentWorkspace = computed(() => workspaceStore.currentWorkspace)
const enterpriseId = computed(() => workspaceStore.enterpriseId)

// 用户信息
const userInfo = computed(() => userStore.userInfo)

const dropMenuInfo = computed(() => userStore.dropMenuInfo)

const currentEnterprise = computed(() => dropMenuInfo.value.enterpriseAmount?.find(item => item.enterpriseId
  === currentWorkspace.value.workspaceId))

const isVipUser = computed(() => {
  return currentEnterprise.value?.accountType === 1
})

const changeEnterpriseOptions = [
  {
    key: 'workspace',
    type: 'render',
    render: () => <UserDropEnterpriseList onEnter={() => onWorkspaceListEnter()} onLeave={() => onWorkspaceListLeave()} />,
  },
]

function onLogout() {
  logout().then(() => {
    authStore.removeToken()
    userStore.$reset()
    workspaceStore.$reset()
    // router.push('/login')
    window.location.href = '/login'
  }).catch((error) => {
    console.log(error)
  })
}

function jumpPersonalCenter() {
  if (route.name === 'PersonalCenterBasicInfoView') {
    return
  }
  openWindowWithWorkspace('/personal-center')
}

function jumpEnergyPoint() {
  if (route.name === 'PersonalCenterEnergyPointInfoView') {
    return
  }
  openWindowWithWorkspace('/personal-center/energy-point-info')
}

function jumpManagement() {
  if (route.name === 'PersonalCenterCompanyManagementView') {
    return
  }
  openWindowWithWorkspace('/personal-center/company-management')
}

// 邀请企业成员
function onInviteMember() {
  emits('inviteMember')
}

const isDropContentHover = ref(false)
const isWorkspaceListHover = ref(false)

function onWorkspaceListEnter() {
  isWorkspaceListHover.value = true
}

function onWorkspaceListLeave() {
  isWorkspaceListHover.value = false

  setTimeout(() => {
    if (!isDropContentHover.value) {
      emits('Leave')
    }
  }, 200)
}

function onMenuListEnter() {
  isDropContentHover.value = true
  emits('Enter')
}

function onMenuListLeave() {
  isDropContentHover.value = false

  setTimeout(() => {
    if (!isWorkspaceListHover.value) {
      emits('Leave')
    }
  }, 200)
}

// 无空间时只显示头像、姓名、帮助中心、退出登录
const showBasicInfo = computed(() => {
  const hasWorkspace = workspaceStore.workspaceList.find(item => item.workspaceType !== WorkspaceType.Check)
  return workspaceStore.workspaceList.length && hasWorkspace
})
</script>

<template>
  <div class="user-drop-content-info" @mouseenter="onMenuListEnter" @mouseleave="onMenuListLeave">
    <div class="header">
      <NAvatar :size="40" class="avartar" round :src="userInfo.headerImg || userInfo.avatar || defaultAvatar" />
      <div
        class="account-info" :data-ph-capture-attribute-page-actions="$getPosthogPath(['顶部导航栏', '个人中心'])"
        @click="jumpPersonalCenter"
      >
        <div class="name">
          {{ userInfo.nickName }}
          <div class="arrow" />
        </div>
        <div class="space ellipsis">
          {{ workspaceStore.workspaceList.length ? (isPersonal ? '个人工作台' : currentWorkspace.workspaceName) : '' }}
        </div>
      </div>
    </div>

    <template v-if="showBasicInfo">
      <div v-if="isPersonal" class="persional-info base-info">
        <div class="item">
          <div class="number">
            {{ dropMenuInfo.balance }}
          </div>
          <div class="label">
            剩余能量点
          </div>
        </div>
        <div class="item">
          <div class="number">
            {{ dropMenuInfo.expiringSoonAmount }}
          </div>
          <div class="label">
            即将过期能量点
          </div>
        </div>
      </div>

      <div v-if="!isPersonal" class="enterprise-info base-info">
        <div class="item">
          <div class="number">
            {{ isVipUser ? '9999+' : (currentEnterprise?.balance || 0) }}
          </div>
          <div class="label">
            可用能量点
          </div>
        </div>
        <div class="item">
          <div class="number">
            {{ currentEnterprise?.archiveCount || 0 }}
          </div>
          <div class="label">
            企业产品数
          </div>
        </div>
        <div class="item">
          <div class="number">
            {{ currentEnterprise?.memberCount || 0 }}
          </div>
          <div class="label">
            企业成员
          </div>
        </div>
      </div>

      <NPopover trigger="hover" placement="left" raw show-arrow>
        <template #trigger>
          <button class="menu-btn">
            <i class="icon apply-icon" />
            <span>
              联系客服
            </span>
          </button>
        </template>
        <div class="apply-popover">
          <img class="img" :src="coworkCodeImage">
          <div>
            扫描二维码 联系客服
          </div>
        </div>
      </NPopover>

      <button
        v-if="!isPersonal" class="menu-btn"
        :data-ph-capture-attribute-page-actions="$getPosthogPath(['顶部导航栏', '邀请成员'])" @click="onInviteMember"
      >
        <i class="icon invite-icon" />
        <span>
          邀请成员
        </span>
      </button>

      <button
        v-if="!isPersonal" class="menu-btn"
        :data-ph-capture-attribute-page-actions="$getPosthogPath(['顶部导航栏', '企业管理'])" @click="jumpManagement"
      >
        <i class="icon enterprise-icon" />
        <span>
          企业管理
        </span>
      </button>

      <button
        v-if="isPersonal" class="menu-btn"
        :data-ph-capture-attribute-page-actions="$getPosthogPath(['顶部导航栏', '能量点明细'])" @click="jumpEnergyPoint"
      >
        <i class="icon energy-point-icon" />
        <span>
          能量点明细
        </span>
      </button>

      <!-- <NDropdown trigger="hover" placement="left-start" :options="changeEnterpriseOptions">
        <button class="menu-btn">
          <i class="icon change-erterprise-icon" />
          <span>
            切换团队
          </span>
          <i class="arrow" />
        </button>
      </NDropdown> -->
    </template>

    <div class="divider" />

    <a
      class="menu-btn helper-center"
      href="https://materialdb.zhaoyl.com/fed/cowork/AI%20Cowork%E4%BD%BF%E7%94%A8%E6%89%8B%E5%86%8C-20250307.pdf"
      target="_blank" :data-ph-capture-attribute-page-actions="$getPosthogPath(['顶部导航栏', '帮助中心'])"
    >
      <i class="icon helper-icon" />
      <span>
        帮助中心
      </span>
    </a>

    <button
      class="menu-btn" :data-ph-capture-attribute-page-actions="$getPosthogPath(['顶部导航栏', '退出登录'])"
      @click="onLogout"
    >
      <i class="icon logout-icon" />
      <span>
        退出登录
      </span>
    </button>
  </div>
</template>

<style lang="scss" scoped>
.apply-popover {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 12px;
  background: linear-gradient(180deg, #FDFEFF 0%, #FFFFFF 100%);
  box-shadow: 0px 8px 12px 0px rgba(202, 207, 227, 0.57);
  border-radius: 8px;
  font-size: 12px;
  color: var(--color--main);
  line-height: 14px;

  .img {
    width: 118px;
    height: 118px;
    margin-bottom: 12px;
  }
}

.user-drop-content-info {
  padding: 16px 8px 8px;

  .header {
    padding-left: 8px;
    display: flex;
    // align-items: center;
    cursor: pointer;
    margin-bottom: 18px;

    .avartar {
      height: 40px;
      margin-right: 12px;
    }

    .account-info {
      display: flex;
      flex: 1;
      flex-direction: column;
      justify-content: center;
      min-width: 0;
      margin-right: 8px;

      .name {
        display: flex;
        align-items: center;
        font-size: 14px;
        font-weight: 600;
        color: #242F57;

        .arrow {
          margin-left: 6px;
          width: 16px;
          height: 16px;
          background: url('./icons/arrow-right.png');
          background-size: cover;
        }
      }

      .space {
        color: #425466;
        font-size: 12px;
      }

    }

  }

  .base-info {
    width: 264px;
    height: 66px;
    background: linear-gradient(257deg, #F5F1FF 0%, #F0F5FF 100%);
    border-radius: 4px 4px 4px 4px;
    margin-bottom: 12px;
    display: flex;

    .item {
      flex: 1;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;

      .number {
        margin-bottom: 4px;
        font-weight: 600;
        font-size: 14px;
        color: #242F57;
        line-height: 16px;
      }

      .label {

        font-weight: 400;
        font-size: 12px;
        color: #8FA1B2;
        line-height: 14px;
      }
    }
  }

  .divider {
    margin: 6px 0;
    height: 1px;
    background: #E8EFFD;
  }

  .menu-btn {
    position: relative;
    width: 100%;
    padding: 0 9px;
    display: flex;
    align-items: center;
    height: 40px;
    border-radius: 4px 4px 4px 4px;
    color: #425466;

    &:hover {
      background: #F0F5FF;

    }

    .icon {
      margin-right: 8px;
      width: 16px;
      height: 16px;
      background-size: cover;
    }

    .arrow {
      position: absolute;
      width: 16px;
      height: 16px;
      background-image: url('./icons/menu-arrow.png');
      background-size: cover;
      right: 4px;
    }

    .apply-icon {
      background-image: url('./icons/menu-apply.png');
    }

    .enterprise-icon {
      background-image: url('./icons/menu-enterprise.png');

    }

    .logout-icon {
      background-image: url('./icons/memu-logout.png');
    }

    .energy-point-icon {
      background-image: url('./icons/menu-energy-icon.png');
    }

    .change-erterprise-icon {
      background-image: url('./icons/menu-change.png');
    }

    .invite-icon {
      background-image: url('./icons/menu-invite.png');
    }

    .helper-icon {
      background-image: url('./icons/menu-helper.png');
    }
  }
}
</style>
